import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface NavItem {
  label: string;
  to: string;
}

const navItems: NavItem[] = [
  { label: "首页", to: "/" },
  { label: "剧情介绍", to: "/category" },
  { label: "角色信息", to: "/#characters" },
  { label: "剧评", to: "/#reviews" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const isActive = (to: string) => {
    const base = to.split("#")[0];
    if (base === "/") return location.pathname === "/" && !location.hash;
    return location.pathname.startsWith(base);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/90 backdrop-blur-md border-b border-background-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer group">
          <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-600 text-accent-300 text-lg transition-transform group-hover:scale-105">
            <i className="ri-movie-2-line" />
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-heading font-bold text-foreground-950 text-base tracking-wide">
              美国式禁忌2
            </span>
            <span className="text-[10px] text-foreground-500 tracking-[0.2em] mt-0.5">
              AMERICAN TABOO II
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-3.5 py-2 text-sm rounded-md whitespace-nowrap cursor-pointer transition-colors ${
                isActive(item.to)
                  ? "text-accent-400 font-medium"
                  : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <form
            onSubmit={submitSearch}
            className="flex items-center h-10 pl-3 pr-1 rounded-md bg-background-100 border border-background-200 focus-within:border-primary-500 transition-colors"
          >
            <i className="ri-search-line text-foreground-500 text-base" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索剧集 / 角色 / 剧评"
              className="bg-transparent outline-none border-none text-sm text-foreground-900 placeholder:text-foreground-500 px-2 w-44 xl:w-56"
              aria-label="站内搜索"
            />
            <button
              type="submit"
              className="h-8 px-3 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              搜索
            </button>
          </form>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <Link
            to="/search"
            aria-label="搜索"
            className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors"
          >
            <i className="ri-search-line text-xl" />
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen(true)}
            className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors"
          >
            <i className="ri-menu-3-line text-2xl" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] bg-background-50 animate-fade-in">
          <div className="flex items-center justify-between px-4 h-16 border-b border-background-200">
            <span className="font-heading font-bold text-foreground-950 text-base">
              美国式禁忌2
            </span>
            <button
              type="button"
              aria-label="关闭菜单"
              onClick={() => setMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-close-line text-2xl" />
            </button>
          </div>
          <div className="p-4">
            <form
              onSubmit={submitSearch}
              className="flex items-center h-12 pl-4 pr-1 rounded-md bg-background-100 border border-background-200"
            >
              <i className="ri-search-line text-foreground-500 text-lg" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索剧集 / 角色 / 剧评"
                className="flex-1 bg-transparent outline-none border-none text-base text-foreground-900 placeholder:text-foreground-500 px-2"
                aria-label="站内搜索"
              />
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
          <nav className="flex flex-col px-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-4 text-base rounded-md flex items-center justify-between cursor-pointer ${
                  isActive(item.to)
                    ? "text-accent-400"
                    : "text-foreground-800 hover:bg-background-100"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-500" />
              </Link>
            ))}
            <Link
              to="/feedback"
              className="px-4 py-4 text-base rounded-md flex items-center justify-between text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              用户反馈
              <i className="ri-arrow-right-s-line text-foreground-500" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}